/**
 * views/MainDashboard.tsx
 * ─────────────────────────
 * RBAC-aware landing dashboard for the Operations Console.
 *
 * Pilot role
 * ──────────
 *   Calls GET /api/missions/mine  (server already filters to the caller's user_id).
 *   Renders one card per active/pending mission showing warehouse, allocated
 *   aisles, and the exact location count the pilot is responsible for.
 *
 * Manager / Admin / Super Admin roles
 * ────────────────────────────────────
 *   Calls GET /api/manage/dashboard.
 *   Renders one card per active warehouse with three readiness badges:
 *     • Locations Uploaded
 *     • SOH Current  (backend verifies created_at date matches today UTC)
 *     • SKU Master Uploaded
 *   Mission coverage bar and pilot-assignment status per warehouse.
 *   A shadcn Alert ("Mission Assignment Status") surfaces any gaps:
 *     missing pilot, zero missions, or < 100% coverage.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Building2,
  MapPin,
  User,
  CalendarClock,
  RefreshCw,
  PackageCheck,
  Database,
  Layers,
} from 'lucide-react';

import { useAuth }      from '@/contexts/AuthContext';
import { apiClient, ApiError } from '@/api/client';
import type { Mission, MissionStatus, WarehouseDashboardItem } from '@/types';

import { Badge }                                    from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle }      from '@/components/ui/alert';
import { Button }                                   from '@/components/ui/button';

// ── Shared helpers ────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<MissionStatus, 'default' | 'success' | 'warning' | 'muted'> = {
  PENDING:   'warning',
  ACTIVE:    'success',
  COMPLETED: 'default',
  CANCELLED: 'muted',
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Small reusable pieces ─────────────────────────────────────────────────────

/** Thin full-width progress bar. */
function CoverageBar({ pct }: { pct: number }) {
  const fill =
    pct === 100 ? 'bg-green-500' :
    pct >= 60   ? 'bg-amber-400' :
                  'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${fill}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">
        {pct}%
      </span>
    </div>
  );
}

/** One three-state readiness badge (checked / unchecked). */
function ReadinessBadge({
  ok,
  label,
  icon: Icon,
}: {
  ok: boolean;
  label: string;
  icon: React.ElementType;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${
        ok
          ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
          : 'bg-gray-50 text-gray-400 ring-1 ring-gray-200'
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {ok ? (
        <CheckCircle2 className="h-3 w-3 shrink-0" />
      ) : (
        <span className="h-3 w-3 shrink-0 rounded-full border border-current" />
      )}
      <span>{label}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MANAGER / ADMIN / SUPER ADMIN VIEW
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Determines whether a warehouse has any assignment gap:
 *   - no locations uploaded yet
 *   - no active/pending missions
 *   - at least one mission without a pilot
 *   - location coverage < 100 %
 */
function warehouseHasGap(w: WarehouseDashboardItem): boolean {
  if (!w.is_active) return false;
  return (
    !w.locations_uploaded ||
    w.missions_active === 0 ||
    w.missions_without_pilot > 0 ||
    w.coverage_pct < 100
  );
}

function gapReasons(w: WarehouseDashboardItem): string[] {
  const reasons: string[] = [];
  if (!w.locations_uploaded)      reasons.push('no location master uploaded');
  if (w.missions_active === 0)    reasons.push('no active or pending missions');
  if (w.missions_without_pilot > 0)
    reasons.push(
      `${w.missions_without_pilot} mission${w.missions_without_pilot > 1 ? 's' : ''} ` +
      `without a pilot assigned`,
    );
  if (w.locations_uploaded && w.coverage_pct < 100)
    reasons.push(`${w.coverage_pct}% of locations covered (${100 - w.coverage_pct}% unassigned)`);
  return reasons;
}

function AssignmentAlert({ items }: { items: WarehouseDashboardItem[] }) {
  const activeWarehouses = items.filter(w => w.is_active);
  const gaps = activeWarehouses.filter(warehouseHasGap);
  const allGood = gaps.length === 0 && activeWarehouses.length > 0;

  if (activeWarehouses.length === 0) return null;

  if (allGood) {
    return (
      <Alert variant="success" className="mb-5">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Mission Assignment Status — All Clear</AlertTitle>
        <AlertDescription>
          Every active warehouse has 100% location coverage and all missions
          have a pilot assigned. You're ready to fly.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="warning" className="mb-5">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Mission Assignment Status</AlertTitle>
      <AlertDescription>
        <p className="mb-2">
          {gaps.length} warehouse{gaps.length > 1 ? 's have' : ' has'} assignment
          gaps — not all locations, aisles, and warehouses are assigned to at
          least one pilot.
        </p>
        <ul className="space-y-1.5">
          {gaps.map(w => (
            <li key={w.warehouse_id} className="flex flex-col gap-0.5">
              <span className="font-medium">
                {w.warehouse_name}{' '}
                <code className="rounded bg-amber-100 px-1 text-xs">{w.warehouse_code}</code>
              </span>
              <span className="text-amber-700 text-xs">
                {gapReasons(w).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function WarehouseCard({ w }: { w: WarehouseDashboardItem }) {
  return (
    <Card className={`bg-white ${!w.is_active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-1.5 text-base truncate">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              {w.warehouse_name}
            </CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              <code className="rounded bg-muted px-1">{w.warehouse_code}</code>
              {' '}·{' '}
              {w.location_count > 0
                ? `${w.location_count.toLocaleString()} locations`
                : 'no locations'}
            </p>
          </div>
          <Badge variant={w.is_active ? 'success' : 'muted'} className="shrink-0 text-xs">
            {w.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Readiness badges */}
        <div className="flex flex-wrap gap-2">
          <ReadinessBadge
            ok={w.locations_uploaded}
            label="Locations Uploaded"
            icon={Layers}
          />
          <ReadinessBadge
            ok={w.soh_today}
            label="SOH Current"
            icon={Database}
          />
          <ReadinessBadge
            ok={w.sku_uploaded}
            label="SKU Master"
            icon={PackageCheck}
          />
        </div>

        {/* Mission coverage */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Mission coverage</span>
            <span>
              {w.missions_active} mission{w.missions_active !== 1 ? 's' : ''}
              {w.missions_without_pilot > 0 && (
                <span className="ml-1 text-amber-600 font-medium">
                  · {w.missions_without_pilot} unassigned
                </span>
              )}
            </span>
          </div>
          {w.locations_uploaded ? (
            <CoverageBar pct={w.coverage_pct} />
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Upload a location master to enable coverage tracking.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ManagerView() {
  const [items,   setItems]   = useState<WarehouseDashboardItem[] | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<WarehouseDashboardItem[]>('/api/manage/dashboard');
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading warehouse status…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load dashboard</AlertTitle>
        <AlertDescription>
          {error}
          <Button
            variant="ghost"
            size="sm"
            className="ml-3 h-6 px-2 text-xs"
            onClick={load}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Building2 className="mb-3 h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm font-medium">No warehouses yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Create a warehouse in the Warehouses section to get started.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Warehouse Readiness
        </h2>
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={load}>
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>

      {/* Assignment status alert — always visible above the cards */}
      <AssignmentAlert items={items} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map(w => (
          <WarehouseCard key={w.warehouse_id} w={w} />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PILOT VIEW
// ══════════════════════════════════════════════════════════════════════════════

function MissionCard({ mission }: { mission: Mission }) {
  const hasSchedule = mission.scheduled_start || mission.scheduled_end;

  return (
    <Card className="bg-white">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">
            {mission.title ?? (
              <code className="text-sm text-muted-foreground">{mission.id.slice(0, 8)}…</code>
            )}
          </CardTitle>
          <Badge variant={STATUS_VARIANT[mission.status]} className="shrink-0 text-xs">
            {mission.status}
          </Badge>
        </div>
        {mission.warehouse_name && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
            <Building2 className="h-3 w-3 shrink-0" />
            {mission.warehouse_name}
            {mission.warehouse_id && (
              <code className="ml-1 rounded bg-muted px-1">
                {mission.warehouse_id.slice(0, 8)}
              </code>
            )}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Aisle chips */}
        {mission.allocated_aisles.length > 0 ? (
          <div className="space-y-1">
            <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Allocated Aisles
            </p>
            <div className="flex flex-wrap gap-1">
              {[...mission.allocated_aisles].sort().map(a => (
                <code
                  key={a}
                  className="rounded bg-primary/8 px-1.5 py-0.5 text-xs font-mono text-primary"
                >
                  {a}
                </code>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No scope restriction — you may scan any location in this warehouse.
          </p>
        )}

        {/* Location count */}
        {mission.location_count > 0 && (
          <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700 ring-1 ring-blue-200">
            <Layers className="h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{mission.location_count.toLocaleString()}</strong> location
              {mission.location_count !== 1 ? 's' : ''} assigned to you
            </span>
          </div>
        )}

        {/* Notes */}
        {mission.notes && (
          <p className="text-xs text-muted-foreground border-t pt-2">{mission.notes}</p>
        )}

        {/* Schedule */}
        {hasSchedule && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t pt-2">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
            <span>
              {fmtDate(mission.scheduled_start)}
              {mission.scheduled_end && (
                <> → {fmtDate(mission.scheduled_end)}</>
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PilotView() {
  const { user }                          = useAuth();
  const [missions, setMissions]           = useState<Mission[] | null>(null);
  const [error,    setError]              = useState<string | null>(null);
  const [loading,  setLoading]            = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<Mission[]>('/api/missions/mine');
      setMissions(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load missions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading your missions…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load missions</AlertTitle>
        <AlertDescription>
          {error}
          <Button variant="ghost" size="sm" className="ml-3 h-6 px-2 text-xs" onClick={load}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Your Assigned Missions
          </h2>
          {user?.name && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              {user.name}
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={load}>
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>

      {missions === null || missions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MapPin className="mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm font-medium">No missions assigned yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your operations manager hasn't created a mission for you yet.
            Check back soon.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {missions.map(m => (
            <MissionCard key={m.id} mission={m} />
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// RBAC ENTRY POINT
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Top-level dashboard that adapts its content to the authenticated user's role.
 *
 * Pilot              → <PilotView>    server-filtered mission cards
 * Manager / Admin    → <ManagerView>  warehouse readiness + assignment alert
 */
export default function MainDashboard() {
  const { user } = useAuth();

  if (user?.role === 'pilot') {
    return <PilotView />;
  }

  return <ManagerView />;
}
